import { useState } from "react";
import { SpaceType as ConstSpaceType, SpaceTypeT } from "src/pages/product/product-types";

import { useQueryClient } from "@tanstack/react-query";
import { decommaizeNumber } from "@toss/utils";
import { UnionServiceType } from "src/api/adminMemo/adminmemo-types";
import { useApiOperation } from "src/api/hooks";
import { saveReservationPolicyAsync } from "src/api/product/product-api";
import { ReservationPolicySaveParams } from "src/api/product/product-types";
import useGetReservationPolicy, { RESERVATION_POLICY_QUERY_KEY } from "src/hooks/product/useGetReservationPolicy";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";
import { findDefaultReservationPolicy } from "../utils/reservationPolicy";
import LimitFrom from "./LimitFrom";

export const SpaceTypeReqDataMap = {
  [ConstSpaceType.MEETING_ROOM]: {
    isUnLimitReservation: "isUnlimitedMeetingRoom",
    isMonthlyUnLimit: "isLimitedMeetingRoomMonthlyReservation",
    monthlyTime: "meetingRoomMaxReservTimeMonth",
    isDailyUnLimit: "isLimitedMeetingRoomDailyReservation",
    dailyTime: "meetingRoomMaxReservTimeDay",
    isAllowOverReservation: "isAllowedMeetingRoomOverReservation",
    contractTotalTime: "meetingRoomTotalTime", // TotalTime을 월별 예약 가능 시간으로 사용
    contractDailyTime: "meetingRoomDailyTime",
  },
  [ConstSpaceType.DESK]: {
    isUnLimitReservation: "isUnlimitedDesk",
    isMonthlyUnLimit: "isLimitedDeskMonthlyReservation",
    monthlyTime: "deskMaxReservTimeMonth",
    isDailyUnLimit: "isLimitedDeskDailyReservation",
    dailyTime: "deskMaxReservTimeDay",
    isAllowOverReservation: "isAllowedDeskOverReservation",
    contractTotalTime: "deskTotalTime",
    contractDailyTime: "deskDailyTime",
  },
  [ConstSpaceType.REFRESH_ROOM]: {
    isUnLimitReservation: "isUnlimitedRefreshRoom",
    isMonthlyUnLimit: "isLimitedRefreshRoomMonthlyReservation",
    monthlyTime: "refreshRoomMaxReservTimeMonth",
    isDailyUnLimit: "isLimitedRefreshRoomDailyReservation",
    dailyTime: "refreshRoomMaxReservTimeDay",
    isAllowOverReservation: "isAllowedRefreshRoomOverReservation",
    contractTotalTime: "refreshRoomTotalTime",
    contractDailyTime: "refreshRoomDailyTime",
  },
} as const;

interface Props {
  spaceType: SpaceTypeT;
  serviceType: UnionServiceType;
  serviceId: string;
  isEditable?: boolean;
  isFeeAvailable?: boolean;
}

const AllSpaceUsageLimitModal = ({ spaceType, serviceType, serviceId, isEditable, isFeeAvailable }: Props) => {
  const isProductPage = serviceType === "SERVICE_PRODUCT";

  const { setBaseModal } = useModal();
  const { setAbstractModalZ1 } = useModal();
  const { openToast } = useToast();
  const queryClient = useQueryClient();
  const { executeAsync: saveReservationPolicy } = useApiOperation(saveReservationPolicyAsync);

  // RRVP03/09 로 기본 이용한도 정책 조회
  const { data: usageLimitPolicyList } = useGetReservationPolicy({ owner: isProductPage ? { productId: serviceId } : { contractManageId: serviceId }, policyType: "usage-limit-policy" });

  const defaultPolicy = findDefaultReservationPolicy(usageLimitPolicyList, "usage-limit-policy", spaceType);
  const usageLimitPolicy = defaultPolicy?.details?.usageLimitPolicy;

  const isDisabledPolicy = usageLimitPolicy?.disabled ?? true;

  const [editObj, setEditObj] = useState({
    isUnLimitReservation: isDisabledPolicy,
    isTimeEnabled: !isDisabledPolicy && usageLimitPolicy?.time ? !usageLimitPolicy.time.disabled : false,
    isMonthlyUnLimit: !isDisabledPolicy && usageLimitPolicy?.time?.monthlyLimit ? !usageLimitPolicy.time.monthlyLimit.disabled : false,
    monthlyTime: usageLimitPolicy?.time?.monthlyLimit?.minutes ?? "",
    isDailyUnLimit: !isDisabledPolicy && usageLimitPolicy?.time?.dailyLimit ? !usageLimitPolicy.time.dailyLimit.disabled : false,
    dailyTime: usageLimitPolicy?.time?.dailyLimit?.minutes ?? "",
    isFeeEnabled: !isDisabledPolicy && usageLimitPolicy?.fee ? !usageLimitPolicy.fee.disabled : false,
    isMonthlyFeeEnabled: !isDisabledPolicy && usageLimitPolicy?.fee?.monthlyLimit ? !usageLimitPolicy.fee.monthlyLimit.disabled : false,
    monthlyFee: usageLimitPolicy?.fee?.monthlyLimit?.amount ?? "",
    isDailyFeeEnabled: !isDisabledPolicy && usageLimitPolicy?.fee?.dailyLimit ? !usageLimitPolicy.fee.dailyLimit.disabled : false,
    dailyFee: usageLimitPolicy?.fee?.dailyLimit?.amount ?? "",
  });

  const handleSubmit = async () => {
    const isDisabled = editObj.isUnLimitReservation;
    const monthlyMinutes = editObj.monthlyTime.toString() === "" ? 0 : decommaizeNumber(editObj.monthlyTime.toString());
    const dailyMinutes = editObj.dailyTime.toString() === "" ? 0 : decommaizeNumber(editObj.dailyTime.toString());
    // 금액은 RRVP04/10 이 문자열로 받는다
    const monthlyAmount = String(editObj.monthlyFee.toString() === "" ? 0 : decommaizeNumber(editObj.monthlyFee.toString()));
    const dailyAmount = String(editObj.dailyFee.toString() === "" ? 0 : decommaizeNumber(editObj.dailyFee.toString()));

    const requestData: ReservationPolicySaveParams = {
      ...(isProductPage ? { productId: serviceId } : { contractManageId: serviceId }),
      facilityType: spaceType,
      usageLimitPolicy: {
        disabled: isDisabled,
        time: {
          disabled: isDisabled || !editObj.isTimeEnabled,
          monthlyLimit: {
            disabled: isDisabled || !editObj.isTimeEnabled || !editObj.isMonthlyUnLimit,
            minutes: monthlyMinutes,
          },
          dailyLimit: {
            disabled: isDisabled || !editObj.isTimeEnabled || !editObj.isDailyUnLimit,
            minutes: dailyMinutes,
          },
        },
        fee: {
          disabled: isDisabled || !editObj.isFeeEnabled,
          monthlyLimit: {
            disabled: isDisabled || !editObj.isFeeEnabled || !editObj.isMonthlyFeeEnabled,
            amount: monthlyAmount,
          },
          dailyLimit: {
            disabled: isDisabled || !editObj.isFeeEnabled || !editObj.isDailyFeeEnabled,
            amount: dailyAmount,
          },
        },
      },
    };

    await saveReservationPolicy(requestData);
    openToast({ content: "저장 되었습니다.", isSystemToast: true });
    queryClient.invalidateQueries({ queryKey: [RESERVATION_POLICY_QUERY_KEY] });
  };

  return (
    <>
      <LimitFrom
        spaceType={spaceType}
        editObj={editObj as any}
        setEditObj={setEditObj as any}
        onClose={() => {
          setAbstractModalZ1({ isOpen: false });
        }}
        handleSubmit={handleSubmit}
        isEditable={isEditable}
        isDefault={true}
        isFeeAvailable={isFeeAvailable}
      />
    </>
  );
};

export default AllSpaceUsageLimitModal;
